import { AlertTriangle } from "lucide-react";
import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";

import NavigationMenuRouterLink from "@/components/NavigationMenuRouterLink";
import { FullPageSpinner } from "@/components/Spinner";
import {
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuList,
} from "@/components/ui/navigation-menu";
import { GameReport } from "@/gen/graphql";
import { useGame, useGameReport } from "@/routes/game/queries";
import { supportedLanguageCodes } from "@/utils/i18n";
import { useLanguageCode } from "@/utils/language";

export default function GameLiveCoverageLayout() {
    const languageCode = useLanguageCode();

    const { gameId } = useParams<{ gameId: string }>();

    const { game, loading, error, refetch } = useGame(gameId);
    const gameReportResult = useGameReport(
        gameId ?? "",
        languageCode && supportedLanguageCodes.includes(languageCode) ? languageCode : "en"
    );

    useEffect(() => {
        if (!loading && !error && !game) {
            const timeout = setTimeout(() => {
                refetch();
            }, 1000);
            return () => clearTimeout(timeout);
        }
    }, [loading, error, game, refetch]);

    if (!gameId) {
        return null;
    }

    if (error) {
        return <p>Error: {error.message}</p>;
    }

    if (loading || !game) {
        return <FullPageSpinner />;
    }

    const joinPath = (...segments: (string | null)[]) => segments.filter(s => s).join("/");
    const showCommentatorWarning =
        !!languageCode && !(gameReportResult?.game?.report as GameReport)?.commentator;

    return (
        <div className="max-h-screen">
            <NavigationMenu className="mx-4">
                <NavigationMenuList>
                    <NavigationMenuItem>
                        <NavigationMenuRouterLink
                            to={joinPath(`/games/${gameId}/reports`, languageCode)}
                            end
                        >
                            Report
                        </NavigationMenuRouterLink>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                        <NavigationMenuRouterLink
                            to={joinPath(`/games/${gameId}/reports`, languageCode, "feed")}
                        >
                            Feed
                        </NavigationMenuRouterLink>
                    </NavigationMenuItem>
                </NavigationMenuList>
            </NavigationMenu>
            {showCommentatorWarning && (
                <div className="mx-4 mb-4 flex items-center gap-2 rounded bg-yellow-200 px-4 py-2 text-yellow-900">
                    <AlertTriangle className="h-4 w-4 text-yellow-700" />
                    <span>
                        No commentator selected! Please choose a commentator on the Report page.
                    </span>
                </div>
            )}
            <Outlet />
        </div>
    );
}
